'use client';

import { Box, Chip, Stack, Typography } from '@mui/material';
import cx from 'clsx';
import { useLocale } from 'next-intl';
import Image from 'next/image';

import Avatar from '@/components/Avatar';
import { Link } from '@/i18n/navigation';
import colors from '@/styles/themes/colors';
import DateTime from '@/utils/static/DateTime';

import styles from './BlogCard.module.scss';

interface BlogCardProps {
  slug: string;
  image: string;
  bannerImage?: string;
  imageAlt: string;
  date: string;
  readTime: string;
  title: string;
  author: {
    name: string;
    role?: string;
    avatar?: string;
  };
  featured?: boolean;
  featuredLabel?: string;
}

const BlogCard = ({
  slug,
  image,
  bannerImage,
  imageAlt,
  date,
  readTime,
  title,
  author,
  featured = false,
  featuredLabel = 'Featured Blog',
}: BlogCardProps) => {
  const locale = useLocale();
  const formattedDate = DateTime.formatBlogDate(date, locale);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const blogHref = `/blog/${slug}` as any;

  return (
    <Link href={blogHref} className={cx(styles.container, { [styles.featured]: featured })}>
      <Box className={styles.card}>
        <Box className={styles.imageWrapper}>
          <Image
            src={image}
            alt={imageAlt}
            fill
            className={cx(styles.image, { [styles.cardImage]: featured && bannerImage })}
            sizes={featured ? '(min-width: 1440px) 1156px, 330px' : '330px'}
          />
          {featured && bannerImage && <Image src={bannerImage} alt={imageAlt} fill className={styles.bannerImage} />}
          {featured && <Chip label={featuredLabel} className={styles.featuredChip} />}
        </Box>
        <Stack direction="column" className={styles.content}>
          <Stack direction="column" spacing={1.5}>
            <Typography variant="body1" color={colors.graytone200}>
              {formattedDate} - {readTime}
            </Typography>
            <Typography variant="h5" component={featured ? 'h2' : 'h3'} color={colors.white}>
              {title}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1.5} className={styles.author}>
            <Avatar name={author.name} role={author.role} image={author.avatar} />
            <Stack spacing={0.5}>
              <Typography variant="subtitle3" component="p" color={colors.white}>
                {author.name}
              </Typography>
              {author.role && (
                <Typography variant="body2" color={colors.graytone200}>
                  {author.role}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Link>
  );
};

export default BlogCard;
